import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, MapPin, User, Phone, Mail, Filter, Search, Check, X, RefreshCw, ChevronDown, ArrowLeft, CreditCard, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { API } from '../config';
import { TableOccupancy } from './TableOccupancy';
import { TherapistEarnings } from './TherapistEarnings';

const THERAPIST_HOURLY_RATE = 30;

const durationToHours = (d) => {
  const s = (d || '').toLowerCase();
  if (s.includes('2 hour')) return 2;
  if (s.includes('1 hr 30') || s.includes('90')) return 1.5;
  if (s.includes('45')) return 0.75;
  if (s.includes('30 min')) return 0.5;
  return 1;
};

const therapistCostFor = (b) => {
  const therapists = (b.service_name || '').toLowerCase().includes('couple') ? 2 : 1;
  return durationToHours(b.service_duration) * THERAPIST_HOURLY_RATE * therapists;
};

const AdminDashboard = ({ onBack, onLogout }) => {
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwStatus, setPwStatus] = useState(null);

  const changePassword = async () => {
    if (pwForm.next.length < 8) { setPwStatus({ error: 'New password must be at least 8 characters' }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwStatus({ error: 'New passwords do not match' }); return; }
    setPwStatus({ loading: true });
    try {
      await axios.post(`${API}/admin/change-password`, {
        token: sessionStorage.getItem('admin_token'),
        current_password: pwForm.current,
        new_password: pwForm.next
      });
      setPwStatus({ success: 'Password changed successfully' });
      setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => { setShowPwModal(false); setPwStatus(null); }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPwStatus({ error: typeof detail === 'string' ? detail : 'Failed to change password' });
    }
  };

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    todayBookings: 0,
    totalRevenue: 0,
    therapistPay: 0,
    netTakings: 0
  });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (locationFilter !== 'all') params.location = locationFilter;
      if (dateFilter) params.date = dateFilter;

      const response = await axios.get(`${API}/bookings`, { params });
      const data = response.data;
      setBookings(data);

      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const active = data.filter(b => b.status !== 'cancelled');
      const revenue = active.reduce((sum, b) => sum + (b.final_price ?? (b.service_price * (1 - b.discount_applied))), 0);
      const therapistPay = active.reduce((sum, b) => sum + therapistCostFor(b), 0);
      setStats({
        total: data.length,
        pending: data.filter(b => b.status === 'pending').length,
        confirmed: data.filter(b => b.status === 'confirmed').length,
        completed: data.filter(b => b.status === 'completed').length,
        cancelled: data.filter(b => b.status === 'cancelled').length,
        todayBookings: data.filter(b => b.booking_date === today).length,
        totalRevenue: revenue,
        therapistPay,
        netTakings: revenue - therapistPay
      });
    } catch (error) {
      console.error('Failed to load admin bookings:', error);
    }
    setLoading(false);
  }, [statusFilter, locationFilter, dateFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      fetchBookings();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const [payLinkStatus, setPayLinkStatus] = useState({});

  const sendPaymentLink = async (bookingId) => {
    setPayLinkStatus(prev => ({ ...prev, [bookingId]: 'sending' }));
    try {
      const res = await axios.post(`${API}/bookings/${bookingId}/send-payment-link`, null, {
        params: { return_url: window.location.origin + '/booking-confirmed' }
      });
      setPayLinkStatus(prev => ({ ...prev, [bookingId]: 'sent' }));
      try { await navigator.clipboard.writeText(res.data.checkout_url); } catch (e) { /* clipboard optional */ }
    } catch (error) {
      setPayLinkStatus(prev => ({ ...prev, [bookingId]: 'error' }));
      alert(error.response?.data?.detail || 'Failed to send payment link');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.client_name.toLowerCase().includes(search) ||
      booking.client_email.toLowerCase().includes(search) ||
      booking.client_phone.includes(search) ||
      booking.service_name.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IE', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const renderPriceInfo = (booking, finalPrice, balanceDue) => (
    <>
      <p className="font-medium text-stone-800">€{finalPrice.toFixed(2)}</p>
      {booking.online_discount_amount > 0 && (
        <p className="text-xs text-emerald-600">5% online discount (−€{Number(booking.online_discount_amount).toFixed(2)})</p>
      )}
      {booking.discount_applied > 0 && (
        <p className="text-xs text-amber-600">
          {(booking.discount_applied * 100).toFixed(0)}% loyalty discount
        </p>
      )}
      <p className="text-xs text-stone-400">Visit #{booking.visit_number}</p>
      {['full', 'deposit'].includes(booking.payment_type) && (
        booking.payment_status === 'paid' ? (
          <>
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700" data-testid={`payment-badge-${booking.id}`}>
              PAID €{Number(booking.amount_paid || 0).toFixed(2)}
            </span>
            {balanceDue > 0.009 && (
              <p className="text-xs text-amber-700 font-medium mt-0.5" data-testid={`balance-due-${booking.id}`}>
                Collect at studio: €{balanceDue.toFixed(2)}
              </p>
            )}
          </>
        ) : (
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" data-testid={`payment-badge-${booking.id}`}>
            UNPAID
          </span>
        )
      )}
    </>
  );

  const renderActions = (booking) => (
    <div className="flex flex-wrap gap-2">
      {booking.status === 'pending' && (
        <>
          <Button
            size="sm"
            onClick={() => updateStatus(booking.id, 'confirmed')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Check size={14} className="mr-1" />
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateStatus(booking.id, 'cancelled')}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <X size={14} className="mr-1" />
            Cancel
          </Button>
        </>
      )}
      {booking.status === 'confirmed' && (
        <Button
          size="sm"
          onClick={() => updateStatus(booking.id, 'completed')}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Check size={14} className="mr-1" />
          Complete
        </Button>
      )}
      {['full', 'deposit'].includes(booking.payment_type) && booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
        <Button
          size="sm"
          variant="outline"
          disabled={payLinkStatus[booking.id] === 'sending' || payLinkStatus[booking.id] === 'sent'}
          onClick={() => sendPaymentLink(booking.id)}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid={`send-payment-link-${booking.id}`}
        >
          <CreditCard size={14} className="mr-1" />
          {payLinkStatus[booking.id] === 'sending' ? 'Sending...' : payLinkStatus[booking.id] === 'sent' ? 'Link Sent ✓' : 'Send Pay Link'}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <div className="flex items-center gap-1 sm:gap-4 min-w-0">
              <Button variant="ghost" onClick={onBack} className="p-2 shrink-0">
                <ArrowLeft size={20} />
              </Button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-semibold text-stone-800 truncate">Admin Dashboard</h1>
                <p className="text-sm text-stone-500 hidden sm:block">Manage bookings</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Button onClick={fetchBookings} variant="outline" className="gap-2 px-2 sm:px-4">
                <RefreshCw size={16} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button onClick={() => { setShowPwModal(true); setPwStatus(null); }} variant="outline" className="gap-2 px-2 sm:px-4" data-testid="change-password-btn">
                <KeyRound size={16} />
                <span className="hidden sm:inline">Change Password</span>
              </Button>
              {onLogout && (
                <Button onClick={onLogout} variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 sm:px-4" data-testid="admin-logout-btn">
                  Logout
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {showPwModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPwModal(false)} data-testid="change-password-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Change Admin Password</h3>
              <button onClick={() => setShowPwModal(false)} className="text-stone-400 hover:text-stone-600" data-testid="close-password-modal"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <Input type="password" placeholder="Current password" value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="current-password-input" />
              <Input type="password" placeholder="New password (min 8 characters)" value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="new-password-input" />
              <Input type="password" placeholder="Confirm new password" value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" />
              {pwStatus?.error && <p className="text-sm text-red-600" data-testid="password-error">{pwStatus.error}</p>}
              {pwStatus?.success && <p className="text-sm text-green-600" data-testid="password-success">{pwStatus.success}</p>}
              <Button onClick={changePassword} disabled={pwStatus?.loading || !pwForm.current || !pwForm.next || !pwForm.confirm}
                className="w-full bg-stone-800 hover:bg-stone-700 text-white" data-testid="submit-password-change">
                {pwStatus?.loading ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-stone-500">Total</p>
            <p className="text-2xl font-semibold text-stone-800">{stats.total}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-amber-600">Pending</p>
            <p className="text-2xl font-semibold text-amber-700">{stats.pending}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-blue-600">Confirmed</p>
            <p className="text-2xl font-semibold text-blue-700">{stats.confirmed}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-green-600">Completed</p>
            <p className="text-2xl font-semibold text-green-700">{stats.completed}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-red-600">Cancelled</p>
            <p className="text-2xl font-semibold text-red-700">{stats.cancelled}</p>
          </div>
          <div className="bg-sage-50 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-sage-600">Today</p>
            <p className="text-2xl font-semibold text-sage-700">{stats.todayBookings}</p>
          </div>
          <div className="bg-stone-800 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-stone-300">Revenue</p>
            <p className="text-2xl font-semibold text-white">€{stats.totalRevenue.toFixed(0)}</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 shadow-sm" data-testid="stat-therapist-pay">
            <p className="text-sm text-rose-600">Therapist Pay</p>
            <p className="text-2xl font-semibold text-rose-700">€{stats.therapistPay.toFixed(0)}</p>
            <p className="text-[10px] text-rose-400">@ €{THERAPIST_HOURLY_RATE}/hr</p>
          </div>
          <div className="bg-emerald-800 rounded-xl p-4 shadow-sm" data-testid="stat-net-takings">
            <p className="text-sm text-emerald-200">Net Takings</p>
            <p className="text-2xl font-semibold text-white">€{stats.netTakings.toFixed(0)}</p>
            <p className="text-[10px] text-emerald-300">after therapist pay</p>
          </div>
        </div>

        <TableOccupancy />

        <TherapistEarnings />

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, phone..."
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="city_centre">City Centre</SelectItem>
                <SelectItem value="clarehall">Clarehall</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-[180px]"
            />

            {(statusFilter !== 'all' || locationFilter !== 'all' || dateFilter) && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setStatusFilter('all');
                  setLocationFilter('all');
                  setDateFilter('');
                }}
                className="text-stone-500"
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw size={32} className="animate-spin mx-auto text-stone-400 mb-4" />
              <p className="text-stone-500">Loading bookings...</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar size={48} className="mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No bookings found</p>
            </div>
          ) : (
            <>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-stone-100" data-testid="mobile-bookings-list">
              {filteredBookings.map((booking) => {
                const finalPrice = booking.final_price ?? (booking.service_price * (1 - booking.discount_applied));
                const balanceDue = Math.max(0, finalPrice - (booking.amount_paid || 0));
                return (
                  <div key={booking.id} className="p-4" data-testid={`booking-card-${booking.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-800 truncate">{booking.client_name}</p>
                        <a href={`tel:${booking.client_phone}`} className="text-sm text-sage-700 underline">{booking.client_phone}</a>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize shrink-0 ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <p className="text-sm text-stone-700 font-medium">{booking.service_name} <span className="text-stone-400 font-normal">· {booking.service_duration}</span></p>
                    <p className="text-sm text-stone-600">{formatDate(booking.booking_date)} at {booking.booking_time} · <span className="capitalize">{booking.location.replace('_', ' ')}</span></p>
                    {booking.therapist_preference && booking.therapist_preference !== 'no_preference' && (
                      <p className="text-xs text-sage-600 capitalize">Therapist: {booking.therapist_preference}</p>
                    )}
                    <div className="mt-2">
                      {renderPriceInfo(booking, finalPrice, balanceDue)}
                    </div>
                    <div className="mt-3">
                      {renderActions(booking)}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Client</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Service</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Date & Time</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Location</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Price</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredBookings.map((booking) => {
                    const finalPrice = booking.final_price ?? (booking.service_price * (1 - booking.discount_applied));
                    const balanceDue = Math.max(0, finalPrice - (booking.amount_paid || 0));
                    return (
                      <tr key={booking.id} className="hover:bg-stone-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-stone-800">{booking.client_name}</p>
                            <p className="text-sm text-stone-500">{booking.client_email}</p>
                            <p className="text-sm text-stone-500">{booking.client_phone}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-stone-800">{booking.service_name}</p>
                          <p className="text-sm text-stone-500">{booking.service_duration}</p>
                          {booking.therapist_preference && booking.therapist_preference !== 'no_preference' && (
                            <p className="text-xs text-sage-600 capitalize">
                              Therapist: {booking.therapist_preference}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-stone-800">{formatDate(booking.booking_date)}</p>
                          <p className="text-sm text-stone-500">{booking.booking_time}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="capitalize text-stone-700">
                            {booking.location.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {renderPriceInfo(booking, finalPrice, balanceDue)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {renderActions(booking)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Notes Section */}
        {filteredBookings.some(b => b.notes || b.treat_recipient_name) && (
          <div className="mt-6 bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-stone-800 mb-4">Client Notes</h3>
            <div className="space-y-3">
              {filteredBookings
                .filter(b => b.notes || b.treat_recipient_name)
                .map(booking => (
                  <div key={booking.id} className="bg-stone-50 rounded-lg p-4">
                    <p className="font-medium text-stone-700">{booking.client_name}</p>
                    <p className="text-sm text-stone-500">{booking.booking_date} at {booking.booking_time}</p>
                    {booking.notes && <p className="text-stone-600 mt-2">{booking.notes}</p>}
                    {booking.treat_recipient_name && (
                      <p className="text-rose-600 mt-2 text-sm" data-testid={`treat-info-${booking.id}`}>
                        🎁 Treat Her booking for: <strong>{booking.treat_recipient_name}</strong>
                        {booking.treat_message && <> — “{booking.treat_message}”</>}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
